import React, { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Plus, Trash, X } from "@phosphor-icons/react";

export default function ReceivingProcessPage() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    po_number: "", product_id: "", qty_received: "",
    serials: "", substitute_product_id: "", receiving_location_id: "",
    received_date: new Date().toISOString().slice(0, 10),
    project_code: "", notes: "",
  });

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/alm/receipts"), api.get("/alm/products"), api.get("/alm/locations"),
    ]);
    setRows(a.data); setProducts(b.data); setLocations(c.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const serials = form.serials ? form.serials.split(/[\n,]/).map(s => s.trim()).filter(Boolean) : [];
    const payload = {
      ...form,
      qty_received: parseFloat(form.qty_received),
      serials,
      substitute_product_id: form.substitute_product_id || null,
      receiving_location_id: form.receiving_location_id || null,
    };
    const { data } = await api.post("/alm/receipts", payload);
    setOpen(false);
    setForm({ ...form, po_number: "", product_id: "", qty_received: "", serials: "", substitute_product_id: "", project_code: "", notes: "" });
    load();
    if (data.assets_created?.length) {
      alert(`Receipt posted. Auto-created ${data.assets_created.length} asset(s):\n${data.assets_created.join("\n")}`);
    }
  };

  const remove = async (id) => { if (window.confirm("Delete this receipt?")) { await api.delete(`/alm/receipts/${id}`); load(); } };

  return (
    <div>
      <PageHeader
        eyebrow="Asset Lifecycle"
        title="Receiving Process"
        subtitle="Receive against a PO, capture serial numbers, allow substitutes and auto-tag assets."
        testid="alm-receipts-title"
        actions={
          <button onClick={() => setOpen(true)} data-testid="alm-receipt-new-btn" className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] flex items-center gap-2">
            <Plus size={14} weight="bold" /> New Receipt
          </button>
        }
      />
      <div className="p-8">
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="alm-receipts-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["PO #", "Product", "Qty Recv.", "Serials", "Location", "Project", "Received", ""].map(h => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs font-bold">{r.po_number}</td>
                  <td className="px-4 py-3"><span className="font-mono text-xs font-bold">{r.product_code}</span> <span className="text-xs text-gray-600">— {r.product_name}</span></td>
                  <td className="px-4 py-3 font-mono">{r.qty_received}</td>
                  <td className="px-4 py-3 font-mono text-xs">{(r.serials || []).length} SN</td>
                  <td className="px-4 py-3 text-xs">{r.receiving_location_name}</td>
                  <td className="px-4 py-3 text-xs">{r.project_code}</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtDate(r.received_date)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(r.id)} data-testid={`alm-receipt-del-${r.id}`} className="text-[#DC2626] hover:underline text-xs font-bold">
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">No receipts yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setOpen(false)}>
          <form onClick={e => e.stopPropagation()} onSubmit={create} className="w-full max-w-md bg-white h-full overflow-y-auto" data-testid="alm-receipt-form">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Receive</div>
                <h2 className="text-2xl font-black tracking-tight">New Receipt</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">PO Number *</label>
                <input required value={form.po_number} onChange={e => setForm({ ...form, po_number: e.target.value })} data-testid="alm-receipt-po" className="w-full border border-gray-300 px-3 py-2.5 text-sm" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Product *</label>
                <select required value={form.product_id} onChange={e => setForm({ ...form, product_id: e.target.value })} data-testid="alm-receipt-product" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  <option value="">— Select Product —</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Substitute Product (optional)</label>
                <select value={form.substitute_product_id} onChange={e => setForm({ ...form, substitute_product_id: e.target.value })} data-testid="alm-receipt-sub" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  <option value="">— None —</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Receiving Location</label>
                <select value={form.receiving_location_id} onChange={e => setForm({ ...form, receiving_location_id: e.target.value })} data-testid="alm-receipt-loc" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  <option value="">— None —</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
                </select>
              </div>
              {[
                ["qty_received", "Qty Received *", "number", true],
                ["received_date", "Received Date *", "date", true],
                ["project_code", "Project Code", "text", false],
                ["notes", "Notes", "text", false],
              ].map(([k, label, type, required]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">{label}</label>
                  <input type={type} step="any" required={required} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`alm-receipt-${k}`} className="w-full border border-gray-300 px-3 py-2.5 text-sm" />
                </div>
              ))}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Serial Numbers (one per line or comma-sep)</label>
                <textarea rows={3} value={form.serials} onChange={e => setForm({ ...form, serials: e.target.value })} data-testid="alm-receipt-serials" className="w-full border border-gray-300 px-3 py-2.5 text-sm font-mono" />
                <p className="text-[10px] text-gray-500 mt-1">Each serial will auto-create a tracked asset. If empty, assets are created based on quantity.</p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200">
              <button type="submit" data-testid="alm-receipt-submit" className="w-full bg-[#0020C2] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#001AA3]">Post Receipt</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
